<template>
  <div class="dsc" :class="{ 'dsc--compact': compact }">
    <div class="dsc-toolbar">
      <div v-if="showFilter" class="dsc-filter">
        <button
          type="button"
          class="dsc-selbtn"
          :class="{ 'is-active': selectedIds.length > 0 }"
          @click.stop="toggleDropdown"
        >
          <span class="dsc-selbtn-label">{{ filterLabel }}</span>
          <span class="dsc-caret">▾</span>
        </button>
        <div v-if="dropdownOpen" class="dsc-dd" @click.stop>
          <input
            ref="search"
            v-model="search"
            type="text"
            class="dsc-dd-search"
            placeholder="ค้นหาแพทย์..."
          />
          <div v-if="selectedIds.length" class="dsc-dd-head">
            <button
              type="button"
              class="dsc-dd-clear"
              @click="clearSelection"
            >
              ล้างทั้งหมด
            </button>
          </div>
          <div class="dsc-dd-list">
            <div
              v-for="doc in filteredDoctors"
              :key="doc.branchMemberId"
              class="dsc-dd-row"
              :class="{ 'is-on': isSelected(doc) }"
              @click="toggleDoctor(doc)"
            >
              <span class="dsc-dd-box">{{ isSelected(doc) ? "✓" : "" }}</span>
              <span class="dsc-dd-swatch" :style="{ background: doc.color || '#bbb' }"></span>
              <span class="dsc-dd-name">{{ doc.name }}</span>
            </div>
            <div v-if="filteredDoctors.length === 0" class="dsc-dd-empty">ไม่พบแพทย์</div>
          </div>
        </div>
      </div>

      <div class="dsc-monthnav">
        <b-button size="sm" variant="light" @click="stepMonth(-1)">‹</b-button>
        <span class="dsc-monthlabel">{{ monthLabel }}</span>
        <b-button size="sm" variant="light" @click="stepMonth(1)">›</b-button>
      </div>
    </div>

    <DoctorMonthOverview
      :year="viewYear"
      :month="viewMonth"
      :members="members"
      :doctors="doctors"
      :highlight-doctor-ids="selectedIds"
      :selected-date="selectedDate"
      :dense="compact"
      :max-dots="maxDots"
      :loading="loading"
      dots-select-day
      variant="dots"
      @day-click="onDayClick"
    />

    <p v-if="!compact" class="dsc-legend">
      จุดสี = หมอที่ลงตรวจวันนั้น (รวมวันลา/ลงเพิ่มแล้ว) · ชี้ค้างเพื่อดูชื่อ ·
      คลิกวันเพื่อไปดูคิววันนั้น
    </p>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions } from "vuex";
import DoctorMonthOverview from "@/components/workingHours/DoctorMonthOverview.vue";

const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const TH_ABBR = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];

// Month-overview calendar with month nav + batch-schedule fetch/cache and an
// optional fixed-height, searchable doctor highlighter. Shared by the
// QueueManage right sidebar (compact) and the "ตารางแพทย์" modal (full size).
// The host owns the selected date; this component owns the visible month +
// filter selection + fetched data.
export default {
  name: "DoctorScheduleCalendar",
  components: { DoctorMonthOverview },
  props: {
    // normalized doctors: [{ branchMemberId, name, color }]
    doctors: { type: Array, default: () => [] },
    initialDate: { type: String, default: null }, // YYYY-MM-DD, anchors the view
    selectedDate: { type: String, default: null }, // active day ring (host-owned)
    showFilter: { type: Boolean, default: false }, // searchable doctor highlighter
    compact: { type: Boolean, default: false }, // dense layout for the sidebar
    maxDots: { type: Number, default: 4 },
  },
  data() {
    const anchor = this.initialDate ? moment(this.initialDate) : moment();
    return {
      viewYear: anchor.year(),
      viewMonth: anchor.month() + 1,
      selectedIds: [], // highlighted doctor branchMemberIds
      dropdownOpen: false,
      search: "",
      loading: false,
      members: [],
      monthCache: {}, // "YYYY-M" -> members[]
    };
  },
  computed: {
    monthLabel() {
      const months = this.compact ? TH_ABBR : TH_MONTHS;
      return `${months[this.viewMonth - 1]} ${this.viewYear + 543}`;
    },
    monthKey() {
      return `${this.viewYear}-${this.viewMonth}`;
    },
    filterLabel() {
      const ids = this.selectedIds;
      if (ids.length === 0) return "เลือกแพทย์";
      const first = this.doctors.find((d) => d.branchMemberId === ids[0]);
      const name = (first && first.name) || "แพทย์";
      return ids.length === 1 ? name : `${name} +${ids.length - 1}`;
    },
    filteredDoctors() {
      const q = this.search.trim();
      if (!q) return this.doctors;
      return this.doctors.filter((d) => (d.name || "").indexOf(q) !== -1);
    },
  },
  watch: {
    // Re-anchor when the host moves the selected date into another month
    // (e.g. QueueManage's prev/next/today crossing a month boundary).
    initialDate(val) {
      if (!val) return;
      const m = moment(val);
      if (m.year() !== this.viewYear || m.month() + 1 !== this.viewMonth) {
        this.anchor(val);
      }
    },
  },
  created() {
    this.load();
  },
  mounted() {
    document.addEventListener("click", this.closeDropdown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdown);
  },
  methods: {
    ...mapActions({
      fetchBranchSchedule: "moduleWorkingHours/fetchBranchSchedule",
    }),
    isSelected(doc) {
      return this.selectedIds.indexOf(doc.branchMemberId) !== -1;
    },
    toggleDoctor(doc) {
      const i = this.selectedIds.indexOf(doc.branchMemberId);
      if (i === -1) this.selectedIds.push(doc.branchMemberId);
      else this.selectedIds.splice(i, 1);
    },
    clearSelection() {
      this.selectedIds = [];
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.search = "";
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
      }
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    // Point the view at a date's month and (re)load. Public: the finder modal
    // calls this on show() so it always opens on the host's current date.
    anchor(date) {
      const m = date ? moment(date) : moment();
      this.viewYear = m.year();
      this.viewMonth = m.month() + 1;
      this.load();
    },
    stepMonth(delta) {
      const next = moment(`${this.viewYear}-${this.viewMonth}-01`, "YYYY-M-DD").add(
        delta,
        "month"
      );
      this.viewYear = next.year();
      this.viewMonth = next.month() + 1;
      this.load();
    },
    async load() {
      const key = this.monthKey;
      if (this.monthCache[key]) {
        this.members = this.monthCache[key];
        return;
      }
      const start = moment(`${this.viewYear}-${this.viewMonth}-01`, "YYYY-M-DD");
      this.loading = true;
      try {
        const members = await this.fetchBranchSchedule({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          from: start.format("YYYY-MM-DD"),
          to: start.clone().endOf("month").format("YYYY-MM-DD"),
        });
        this.$set(this.monthCache, key, members);
        // Guard against a stale response after the user paged months.
        if (this.monthKey === key) this.members = members;
      } finally {
        this.loading = false;
      }
    },
    onDayClick(dateStr) {
      this.$emit("select-date", dateStr);
    },
  },
};
</script>

<style scoped>
.dsc-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.dsc-filter {
  position: relative;
  flex: 1;
  min-width: 0;
}
.dsc-selbtn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  border: 1px solid #e3e6ec;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  line-height: 1.3;
}
.dsc-selbtn:hover {
  border-color: #4a7cff;
}
.dsc-selbtn.is-active {
  border-color: #1a8a4a;
  background: #f1fbf5;
  color: #1a8a4a;
  font-weight: 600;
}
.dsc-selbtn-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dsc-caret {
  color: #98a0ab;
  font-size: 10px;
}
.dsc-dd {
  position: absolute;
  z-index: 60;
  top: calc(100% + 4px);
  left: 0;
  min-width: 240px;
  max-width: 300px;
  background: #fff;
  border: 1px solid #e3e6ec;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
  padding: 6px;
}
.dsc-dd-search {
  width: 100%;
  border: 1px solid #e3e6ec;
  border-radius: 8px;
  padding: 6px 9px;
  font-size: 12.5px;
  margin-bottom: 6px;
  outline: none;
}
.dsc-dd-search:focus {
  border-color: #4a7cff;
}
.dsc-dd-head {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 4px 6px;
}
.dsc-dd-clear {
  border: none;
  background: none;
  color: #4a7cff;
  cursor: pointer;
  font-size: 11px;
  text-decoration: underline;
  padding: 0;
}
.dsc-dd-list {
  max-height: 220px;
  overflow: auto;
}
.dsc-dd-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 12.5px;
}
.dsc-dd-row:hover {
  background: #f5f7fb;
}
.dsc-dd-box {
  width: 15px;
  height: 15px;
  border: 1.5px solid #c4ccd8;
  border-radius: 4px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #fff;
}
.dsc-dd-row.is-on .dsc-dd-box {
  background: #1a8a4a;
  border-color: #1a8a4a;
}
.dsc-dd-swatch {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dsc-dd-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dsc-dd-empty {
  font-size: 12px;
  color: #98a0ab;
  text-align: center;
  padding: 10px;
}
.dsc-monthnav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dsc-monthlabel {
  font-weight: 600;
  font-size: 14px;
  min-width: 130px;
  text-align: center;
}
.dsc-legend {
  font-size: 11px;
  color: #888;
  margin: 12px 0 0;
}

/* Compact (sidebar) tweaks: tighter toolbar + smaller controls. */
.dsc--compact .dsc-toolbar {
  gap: 6px;
  margin-bottom: 10px;
}
.dsc--compact .dsc-selbtn {
  padding: 4px 8px;
  font-size: 12px;
}
.dsc--compact .dsc-monthnav {
  gap: 3px;
}
.dsc--compact .dsc-monthlabel {
  font-size: 12px;
  min-width: 72px;
}
.dsc--compact .dsc-monthnav >>> .btn {
  padding: 2px 7px;
  line-height: 1;
}
</style>
